import { parsePhoneNumberFromString } from 'libphonenumber-js';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
  addToCart,
  updateSMSNumberConfirmed,
} from '../../../../actions/v3Order';
import formatUSD from '../../../../utils/formatUSD';
import { isJpgImage } from '../../../../utils/imageHelpers';
import {
  isProductAllowed,
  isShippingRestrictedForParent,
} from '../../helpers/internationalRestriction';
import SMSNumberConfirmation from '../regular/subcomponents/SMSNumberConfirmation';
import ProductOptionsSelector from '../regular/subcomponents/ProductOptionsSelector';
import { setCurrentProduct } from '../../../../reducers/productConfigSlice';
import CropOptionsPrepaySelector from '../regular/subcomponents/CropOptionsPrepaySelector';
import {
  ImageProtectionOverlay,
  useImageIntentFriction,
} from '../../shared/protectedImage';
import { imageIntentGalleryOf } from '../../shared/protectedImage/imageIntentGallery';
import {
  backgroundSlotsCount,
  getImageRecords,
  individualRecordsCount,
  productIsAllGroup,
  productPosesCount,
} from '../imageRecords';
import { productNeedsBackgroundSelection } from '../productConfigPredicates';
import { selectCropOptionsPredicate } from '../../../../selectors';
import NoPoseImageMessage from '../NoPoseImageMessage';
import UnsupportedProductConfig from '../UnsupportedProductConfig';
import MultiConfigProductItem from './MultiConfigProductItem';

import {
  BackgroundEditingIndicator,
  MultiConfigBackgroundGrid,
  MultiConfigButton,
  MultiConfigGrid,
  MultiConfigGridItem,
  MultiConfigHeader,
  MultiConfigSubHeader,
  PoseImageBox,
  ProductItemButton,
} from './multiConfigStyles';
import { useForm } from 'react-hook-form';
import RequiredProductForm from '../required-product-form/RequiredProductForm';

// Per-child slot accounting. Pose slots derive from the child's image nodes
// (one record per node, so multiple-image children expose one slot per node);
// backgrounds are an independent per-child total, not tied to any pose.
const childIndividualCount = (child) =>
  individualRecordsCount(getImageRecords(child));

const STEPS = {
  PRODUCT: 0,
  POSE: 1,
  BACKGROUND: 2,
  CROP_OPTION: 3,
};

// Pose picker grid for the POSE step. Group children pick from the student's
// group images only — no fallback to the individual gallery (mirrors the
// regular flow's deliberate no-fallback behavior). Either source coming up
// empty renders the matching NoPoseImageMessage instead of a blank grid.
const MultiConfigPoses = ({ childProduct, onSelectPose }) => {
  const {
    allAvailablePosesForOrder,
    availablePoses,
    studentGallery,
    studentGroupImages,
  } = useSelector((state) => state.additionalData);
  const { getFrictionProps } = useImageIntentFriction();

  const childIsGroup = productIsAllGroup(childProduct);
  let poseSource;
  if (childIsGroup) {
    poseSource = studentGroupImages ?? [];
  } else {
    poseSource =
      studentGallery && studentGallery?.length > 0
        ? studentGallery
        : availablePoses;

    if (allAvailablePosesForOrder?.length > 0) {
      poseSource = allAvailablePosesForOrder;
    }
  }

  // Empty source, either kind: say which photo is missing rather than render an
  // empty grid. Individual children used to fall through to a blank grid while
  // only group children got a message.
  if (!poseSource || poseSource.length === 0) {
    return <NoPoseImageMessage slotIsGroup={childIsGroup} />;
  }

  return (
    <>
      {poseSource?.map((pose) => {
        // TODO: handle group image type
        // `pose.id` is a gallery-image id, which is not always a PicsImage id
        // (DP2 galleries hydrate from the legacy images table), so it travels as
        // metadata like the dashboard tiles rather than as picsImageId.
        //
        // The pool can hold price-list-matched poses from sibling galleries
        // and group photos, which carry their own gallery (see
        // galleryOrderContext); the rest take the hook's Redux gallery.
        const poseContext = {
          ...imageIntentGalleryOf(pose),
          surface: 'multi_config',
          imageKey: pose.url || pose.id,
          metadata: { image_id: pose.id },
        };

        return (
          <MultiConfigGridItem
            key={pose.id}
            onClick={() => {
              onSelectPose(pose);
            }}
          >
            <PoseImageBox>
              <img
                src={pose.url}
                alt={pose.name || 'Pose'}
                {...getFrictionProps(poseContext)}
              />
              <ImageProtectionOverlay
                context={poseContext}
                imageSrc={pose.url}
              />
            </PoseImageBox>
            <ProductItemButton>Select & Continue</ProductItemButton>
          </MultiConfigGridItem>
        );
      })}
    </>
  );
};

const MultiConfig = ({ product }) => {
  const dispatch = useDispatch();
  const { selectedStudent } = useSelector((state) => state.additionalData);
  const { getFrictionProps } = useImageIntentFriction({
    surface: 'multi_config',
  });
  const { backgroundOptions } = useSelector((state) => state.shoot);
  const cropOptions = useSelector((state) => state.shoot?.crop_options || []);
  const cropOptionsEnabled = useSelector(
    (state) => state.shoot?.crop_options_enabled || false,
  );
  // Whether a given CHILD of this bundle is offered crop options. Derived from
  // shoot state, so it lives in a selector (selectCropOptionsPredicate) and is
  // read here as a finished value; the child itself comes from the `product`
  // prop, which is why the selector hands back a predicate.
  const shouldShowCropOptions = useSelector(selectCropOptionsPredicate);
  const smsNumberConfirmed = useSelector(
    (state) => state.v3Order.smsNumberConfirmed,
  );
  const { phone, smsPhone } = useSelector((state) => state.parent);
  const parent = useSelector((state) => state.parent);
  const shoot = useSelector((state) => state.shoot.shoot);
  const shippingRestricted = isShippingRestrictedForParent(shoot, parent);
  const currentUserId = useSelector((state) => state.auth.currentUserId);
  const [step, setStep] = useState(STEPS.PRODUCT);
  const [products, setProducts] = useState({}); // [id]: { product, pose, background, cropSelections }
  const [selectedProductId, setSelectedProductId] = useState(null);
  const isPrepay = useSelector((state) => state.shoot.isPrepay);
  const [backgroundsCategoryIndex, setBackgroundsCategoryIndex] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const sortedProducts = Object.values(products).sort(
    (a, b) => a.product.order - b.product.order,
  );
  const productRequiresSMS = product?.child_products?.some?.(
    (prod) => prod.sms_required,
  );
  // Only children that need a background PICKED count toward the requirement.
  // A digital download resolving its own background (all_backgrounds /
  // no_background) reports a positive slot count but must never be asked for one,
  // so counting raw slots required — and routed the customer into — a background
  // choice that gets thrown away, or charged as an upgrade.
  //
  // The combined predicate is the right one here: this flow's own transitions
  // already gate on the slot count, so switching both the count and the
  // transitions to productNeedsBackgroundSelection keeps them in agreement.
  // BYO/YPX now reads the same predicate on both of its sides (getNextUrl and
  // selectBackgroundsSatisfied). Where the two flows still differ is the unit:
  // this one sums each child's slots, while the wizard's background step
  // collects one per child however many it declares.
  const requiredBackgroundsCount = product?.child_products?.reduce(
    (acc, prod) =>
      acc +
      (productNeedsBackgroundSelection(prod) ? backgroundSlotsCount(prod) : 0),
    0,
  );
  const requiredPosesCount = product?.child_products?.reduce(
    (acc, prod) => acc + productPosesCount(prod),
    0,
  );
  const selectedBackgrounds = Object.values(products).reduce((acc, prod) => {
    if (prod.background) return [...acc, prod.background];
    return acc;
  }, []);
  const selectedPoses = Object.values(products).reduce((acc, prod) => {
    if (prod.pose) return [...acc, prod.pose];
    return acc;
  }, []);
  const selectedProductOptionKey = useSelector(
    (state) => state.productConfig.selectedProductOptionKey,
  );
  const selectedProductOptions = useSelector(
    (state) => state.productConfig.selectedProductOptions,
  );
  const { control, handleSubmit, register, setValue } = useForm();

  const canBeSubmitted = () => {
    // Product options are optional - removed validation
    if (
      (selectedBackgrounds.length === requiredBackgroundsCount ||
        backgroundOptions.length === 0) &&
      (isPrepay || selectedPoses.length === requiredPosesCount)
    ) {
      // When crop options are enabled, ensure every child that needs them has complete selections
      if (cropOptionsEnabled && cropOptions.length > 0) {
        const allCropSelectionsComplete = Object.values(products).every(
          (prod) => {
            if (!shouldShowCropOptions(prod.product)) return true;
            const requiredCount = childIndividualCount(prod.product) || 1;
            const selectedCount = (prod.cropSelections || []).filter(
              Boolean,
            ).length;
            return selectedCount >= requiredCount;
          },
        );
        if (!allCropSelectionsComplete) return false;
      }
      return true;
    }
  };

  const handleConfirmSMS = async () => {
    const phoneNumberObject = parsePhoneNumberFromString(
      smsPhone || phone,
      'US',
    );

    if (!phoneNumberObject || !phoneNumberObject.isValid()) {
      Swal.fire('Invalid Phone Number', '', 'error');
      return;
    }
    const result = await Swal.fire({
      title: 'Please Confirm',
      text: `Please confirm ${phoneNumberObject.formatNational()} is the mobile number where we should send your SMS-based products`,
      confirmButtonText: 'OK',
      showCancelButton: true,
      reverseButtons: true,
      focusConfirm: true,
      cancelButtonText: 'Edit',
    });

    if (result.value === true) {
      dispatch(updateSMSNumberConfirmed(true));
      Swal.fire(
        'Confirmed',
        'You may now add the product to your cart',
        'success',
      );
    } else if (result.dismiss === Swal.DismissReason.cancel) {
      setEditMode(true);
    }
  };

  // After a pose is chosen (or immediately, for prepay which has no pose step),
  // go to the background step only when the child carries a background; otherwise
  // fall through to crop options or back to the product grid.
  const advanceAfterPose = (childProduct) => {
    if (
      productNeedsBackgroundSelection(childProduct) &&
      backgroundOptions.length > 0
    ) {
      setStep(STEPS.BACKGROUND);
    } else if (shouldShowCropOptions(childProduct)) {
      setStep(STEPS.CROP_OPTION);
    } else {
      setStep(STEPS.PRODUCT);
    }
  };

  const handleProductClick = (product) => () => {
    setSelectedProductId(product.id);
    if (isPrepay) {
      advanceAfterPose(product);
    } else {
      setStep(STEPS.POSE);
    }
  };

  useEffect(() => {
    if (product.child_products) {
      const newProducts = product.child_products.reduce((acc, prod) => {
        const { id } = prod;
        return {
          ...acc,
          [id]: {
            product: prod,
            background: null,
            pose: null,
            cropSelections: [],
          },
        };
      }, {});
      setProducts(newProducts);
    }
  }, [product]);

  // setCurrentProduct also seeds/reconciles the default option selections.
  useEffect(() => {
    dispatch(setCurrentProduct(product));
  }, [product, dispatch]);

  const addProductToCart = () => {
    handleSubmit((formState) => {
      if (!smsNumberConfirmed && productRequiresSMS) {
        handleConfirmSMS();
      } else {
        const backgroundIds = [];
        const backgroundSelections = [];
        const poseIds = [];
        const poses = [];
        const childCropSelections = [];
        sortedProducts.forEach((prod) => {
          if (prod.background?.id) {
            backgroundIds.push(prod.background.id);
            if (product?.triggers_free_backgrounds) {
              prod.background.price = 0;
            }
            backgroundSelections.push(prod.background);
          }
          if (prod.pose) {
            poseIds.push(prod.pose.id);
            poses.push(prod.pose);
          }
          childCropSelections.push(prod.cropSelections?.filter(Boolean) || []);
        });

        dispatch(
          addToCart({
            studentId: selectedStudent.id,
            fullproductData: product,
            backgroundIds,
            backgroundSelections,
            poseIds,
            poses,
            quantity: 1,
            requiredFields: formState,
            productOptionKey: selectedProductOptionKey,
            productOptions: selectedProductOptions,
            childCropSelections,
          }),
        );
      }
    })();
  };

  const addBackgroundSelection = (background) => () => {
    const newProducts = { ...products };
    if (newProducts[selectedProductId]) {
      newProducts[selectedProductId].background = background;
    }
    setProducts(newProducts);

    const childProduct = newProducts[selectedProductId]?.product;
    if (childProduct && shouldShowCropOptions(childProduct)) {
      setStep(STEPS.CROP_OPTION);
    } else {
      setStep(STEPS.PRODUCT);
    }
  };

  const addPoseSelection = (pose) => {
    const newProducts = { ...products };
    if (newProducts[selectedProductId]) {
      newProducts[selectedProductId].pose = pose;
    }
    setProducts(newProducts);

    const childProduct = newProducts[selectedProductId]?.product;
    const childNeedsBackground =
      productNeedsBackgroundSelection(childProduct) &&
      backgroundOptions?.length > 0;

    if (childNeedsBackground) {
      advanceAfterPose(newProducts[selectedProductId]?.product);
    } else if (childProduct && shouldShowCropOptions(childProduct)) {
      setStep(STEPS.CROP_OPTION);
    } else {
      setStep(STEPS.PRODUCT);
    }
  };

  const handleCropSelectionsChange = (newSelections) => {
    const newProducts = { ...products };
    if (newProducts[selectedProductId]) {
      newProducts[selectedProductId].cropSelections = newSelections;
    }
    setProducts(newProducts);
  };

  const confirmCropSelection = () => {
    setStep(STEPS.PRODUCT);
  };

  const renderBackgroundCategoryTabs = () => {
    if (backgroundOptions.length > 1) {
      return backgroundOptions.map((backgroundCategory, index) => {
        let activeClass = '';

        if (index === backgroundsCategoryIndex) {
          activeClass = 'group-active';
        }
        return (
          <li
            key={backgroundCategory.id}
            className={`bnl-product-config__bg-selector__tab ${activeClass}`}
            onClick={() => setBackgroundsCategoryIndex(index)}
          >
            {backgroundCategory.name}
          </li>
        );
      });
    }
  };

  const renderBackgroundOptions = () => {
    return backgroundOptions[backgroundsCategoryIndex]?.background_images?.map(
      (background) => {
        const backgroundOptionStyle = {
          backgroundImage: `url(${background.image_thumbnail})`,
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
        };

        const lastPose = products[selectedProductId]?.pose;
        // Don't layer JPG images over backgrounds - they don't have alpha transparency
        const shouldShowPoseOverlay =
          !isPrepay && lastPose && !isJpgImage(lastPose.url);
        // Without the layered pose the tile is a bare background swatch —
        // catalogue artwork, not the child — so it stays unprotected. The
        // throttle key is the background so each tile gets its own bucket.
        // The composited pose may be one pooled from a sibling gallery.
        const backgroundContext = {
          ...imageIntentGalleryOf(lastPose),
          surface: 'multi_config',
          imageKey: `background-${background.id}`,
        };

        return (
          <MultiConfigGridItem
            key={background.id}
            style={backgroundOptionStyle}
            onClick={addBackgroundSelection(background)}
          >
            {shouldShowPoseOverlay && (
              <PoseImageBox>
                <img
                  src={lastPose.url}
                  {...getFrictionProps(backgroundContext)}
                  alt="Overlay Pose"
                />
                <ImageProtectionOverlay
                  context={backgroundContext}
                  imageSrc={lastPose.url}
                />
              </PoseImageBox>
            )}
            <ProductItemButton>Choose Background</ProductItemButton>
          </MultiConfigGridItem>
        );
      },
    );
  };

  const renderDirections = () => {
    const productNamePrice = `${product.name} (${formatUSD(
      product.price,
      true,
    )})`;
    const childProductName = products?.[selectedProductId]?.product?.name;
    const defaultDirections = (
      <MultiConfigSubHeader>
        {isPrepay
          ? `${productNamePrice}: Configure each of these products:`
          : `${productNamePrice}: Choose from any pose for each of these products:`}
      </MultiConfigSubHeader>
    );

    switch (step) {
      case STEPS.POSE:
        if (childProductName) {
          return (
            <>
              <MultiConfigSubHeader>{productNamePrice}</MultiConfigSubHeader>
              <MultiConfigSubHeader>{`${childProductName}: please select one of the poses below for this product.`}</MultiConfigSubHeader>
            </>
          );
        }
        return defaultDirections;
      case STEPS.BACKGROUND:
        if (childProductName) {
          return (
            <>
              <MultiConfigSubHeader>{productNamePrice}</MultiConfigSubHeader>
              <MultiConfigSubHeader>{`${childProductName}: please select one of the backgrounds below for this product.`}</MultiConfigSubHeader>
            </>
          );
        }
        return defaultDirections;
      case STEPS.CROP_OPTION:
        if (childProductName) {
          return (
            <>
              <MultiConfigSubHeader>{productNamePrice}</MultiConfigSubHeader>
              <MultiConfigSubHeader>{`${childProductName}: select a crop style for this product.`}</MultiConfigSubHeader>
            </>
          );
        }
        return defaultDirections;
      case STEPS.PRODUCT:
      default:
        return defaultDirections;
    }
  };

  if (shippingRestricted && !isProductAllowed(product, true)) {
    return <Redirect to="/v3/offers" />;
  }

  // Each bundle child holds exactly one pose and one background, so a child
  // needing more of either can never satisfy canBeSubmitted — the customer
  // would configure forever with Add to Cart disabled. Guard with an
  // explanation instead of rendering the dead-end configurator.
  const unsupportedChild = product?.child_products?.find(
    (child) =>
      (!isPrepay && productPosesCount(child) > 1) ||
      (backgroundOptions.length > 0 &&
        productNeedsBackgroundSelection(child) &&
        backgroundSlotsCount(child) > 1),
  );
  if (unsupportedChild) {
    return <UnsupportedProductConfig productName={unsupportedChild.name} />;
  }

  return (
    <div>
      <MultiConfigHeader>
        {isPrepay
          ? 'Configure each item'
          : 'Choose your preferred photo for each item'}
      </MultiConfigHeader>
      {renderDirections()}
      {step === STEPS.BACKGROUND && backgroundOptions?.length > 1 && (
        <ul className="bnl-product-config__bg-selector__tabs-container">
          {renderBackgroundCategoryTabs()}
        </ul>
      )}
      {step === STEPS.BACKGROUND ? (
        <>
          {(() => {
            const bgProducts = sortedProducts.filter((p) =>
              productNeedsBackgroundSelection(p.product),
            );
            const currentIndex = bgProducts.findIndex(
              (p) => p.product.id === selectedProductId,
            );
            const total = bgProducts.length;
            const position = currentIndex + 1;
            if (total > 0 && position > 0) {
              const existing = bgProducts[currentIndex]?.background;
              const action = existing ? 'Editing' : 'Selecting';
              return (
                <BackgroundEditingIndicator>
                  {`${action} background ${position} of ${total}`}
                </BackgroundEditingIndicator>
              );
            }
            return null;
          })()}
          <MultiConfigBackgroundGrid>
            {renderBackgroundOptions()}
          </MultiConfigBackgroundGrid>
        </>
      ) : (
        <MultiConfigGrid>
          {step === STEPS.PRODUCT && (
            <>
              {sortedProducts.map((prod) => (
                <MultiConfigProductItem
                  key={prod.product.id}
                  item={prod}
                  onClick={handleProductClick}
                />
              ))}
            </>
          )}
          {step === STEPS.POSE && (
            <MultiConfigPoses
              childProduct={products[selectedProductId]?.product}
              onSelectPose={addPoseSelection}
            />
          )}
        </MultiConfigGrid>
      )}
      {step === STEPS.CROP_OPTION && (
        <>
          <CropOptionsPrepaySelector
            cropOptions={cropOptions}
            posesCount={
              childIndividualCount(products[selectedProductId]?.product) || 1
            }
            cropSelections={products[selectedProductId]?.cropSelections || []}
            onCropSelectionsChange={handleCropSelectionsChange}
          />
          <MultiConfigButton
            onClick={confirmCropSelection}
            disabled={
              (products[selectedProductId]?.cropSelections || []).filter(
                Boolean,
              ).length <
              (childIndividualCount(products[selectedProductId]?.product) || 1)
            }
          >
            Confirm Crop Style
          </MultiConfigButton>
        </>
      )}
      {canBeSubmitted() && productRequiresSMS && (
        <SMSNumberConfirmation
          currentUserId={currentUserId}
          editMode={editMode}
          setEditMode={setEditMode}
          smsNumber={smsPhone}
          phone={phone}
          smsNumberConfirmed={smsNumberConfirmed}
        />
      )}
      {canBeSubmitted() && (
        <MultiConfigButton onClick={addProductToCart}>
          Done, continue
        </MultiConfigButton>
      )}
      <ProductOptionsSelector />
      <RequiredProductForm
        product={product}
        register={register}
        control={control}
        setValue={setValue}
      />
    </div>
  );
};

export default MultiConfig;
